import { useEffect, useRef, useState, type ReactNode } from "react";
import { getUsersApi } from "@praesto/core";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  FormField,
  Input,
  SectionHeading,
  Select,
  SkeletonFormSection,
} from "@praesto/ui-kit";
import { useActiveHotel, useCore } from "../lib/core";
import { relativeTime, timestampToMillis, type Translate } from "../lib/time";
import {
  connectIntegration,
  disconnectIntegration,
  listIntegrations,
  syncIntegration,
  testIntegration,
  updateIntegrationConfig,
  type ConfigFieldDTO,
  type IntegrationSummaryDTO,
  type SyncIntegrationResult,
  type TestIntegrationResult,
} from "./integrations-client";
import { UploadExportModal } from "./UploadExportModal";

type SaveStatus = "idle" | "saving" | "saved" | "error";
/**
 * Le due nuove non sono intervalli, sono ORE DEL GIORNO.
 *
 * «Ogni giorno» significa «almeno 24 ore dall'ultima volta», quindi deriva: un
 * giro in ritardo sposta il successivo, e dopo una settimana la sincronizzazione
 * cade di notte. «Tre volte al giorno» significa alle 6, alle 16 e alle 21 —
 * prima del turno del mattino, metà pomeriggio, sera — e ci resta.
 */
type Cadence = "hourly" | "operativa" | "mattina_e_sera" | "daily";

/** Field names matching this pattern render as a masked `password` input;
 *  everything else renders as plain `text`. */
const SENSITIVE_FIELD_RE = /secret|password|key|token/i;

/** One row of the `employeeMap` connector config: maps an external employee
 *  id (as returned by the source system, e.g. TeamSystem) to the Praesto
 *  `userId`/`hotelId` pair it should apply records against — see
 *  `employeeMapSchema` in `functions/src/integrations/teamsystem/connector.ts`. */
interface EmployeeMapRow {
  readonly externalEmployeeId: string;
  readonly userId: string;
  readonly hotelId: string;
}

function rowsToEmployeeMap(
  rows: ReadonlyArray<EmployeeMapRow>,
): Record<string, { userId: string; hotelId: string }> {
  const map: Record<string, { userId: string; hotelId: string }> = {};
  for (const row of rows) {
    if (!row.externalEmployeeId) continue;
    map[row.externalEmployeeId] = { userId: row.userId, hotelId: row.hotelId };
  }
  return map;
}

/** Inverse of `rowsToEmployeeMap` — reconstructs editable rows from the
 *  stored `config.employeeMap` (now returned by `GET /api/integrations`) so
 *  a reload shows the owner's existing mappings instead of a blank editor.
 *  Defensive about shape since `config` crosses the wire as `unknown`. */
function employeeMapRowsFromConfig(
  config: Record<string, unknown> | undefined,
): ReadonlyArray<EmployeeMapRow> {
  const raw = config?.["employeeMap"];
  if (!raw || typeof raw !== "object") return [];
  const rows: EmployeeMapRow[] = [];
  for (const [externalEmployeeId, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!value || typeof value !== "object") continue;
    const entry = value as { userId?: unknown; hotelId?: unknown };
    rows.push({
      externalEmployeeId,
      userId: typeof entry.userId === "string" ? entry.userId : "",
      hotelId: typeof entry.hotelId === "string" ? entry.hotelId : "",
    });
  }
  return rows;
}

/**
 * `externalEmployeeId → {userId, hotelId}` table editor for connectors that
 * target `leaves`/`shifts` records keyed by an external id (currently
 * TeamSystem). v1 keeps plain text inputs — no user/hotel picker — the
 * owner is expected to know the ids; a friendlier picker is a fast-follow.
 */
function EmployeeMapEditor(props: {
  readonly rows: ReadonlyArray<EmployeeMapRow>;
  readonly onChange: (rows: ReadonlyArray<EmployeeMapRow>) => void;
  readonly t: Translate;
}) {
  const { rows, onChange, t } = props;

  function updateRow(index: number, patch: Partial<EmployeeMapRow>): void {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function addRow(): void {
    onChange([...rows, { externalEmployeeId: "", userId: "", hotelId: "" }]);
  }

  function removeRow(index: number): void {
    onChange(rows.filter((_, i) => i !== index));
  }

  return (
    <div className="space-y-2">
      <p className="text-eyebrow">{t("settings.integrations.employeeMap.title")}</p>
      <p className="text-caption text-fg-muted">{t("settings.integrations.employeeMap.help")}</p>
      <div className="space-y-2">
        {rows.map((row, index) => (
          <div key={index} className="flex flex-wrap items-end gap-2">
            <FormField label={t("settings.integrations.employeeMap.externalEmployeeId")}>
              <Input
                size="sm"
                value={row.externalEmployeeId}
                onChange={(e) => updateRow(index, { externalEmployeeId: e.target.value })}
              />
            </FormField>
            <FormField label={t("settings.integrations.employeeMap.userId")}>
              <Input
                size="sm"
                value={row.userId}
                onChange={(e) => updateRow(index, { userId: e.target.value })}
              />
            </FormField>
            <FormField label={t("settings.integrations.employeeMap.hotelId")}>
              <Input
                size="sm"
                value={row.hotelId}
                onChange={(e) => updateRow(index, { hotelId: e.target.value })}
              />
            </FormField>
            <Button size="sm" variant="ghost" onClick={() => removeRow(index)}>
              {t("settings.integrations.employeeMap.removeRow")}
            </Button>
          </div>
        ))}
      </div>
      <Button size="sm" variant="secondary" onClick={addRow}>
        {t("settings.integrations.employeeMap.addRow")}
      </Button>
    </div>
  );
}

/** `lastRunAt` crosses the wire as a serialized Firestore `Timestamp`
 *  (`{_seconds, _nanoseconds}` — `JSON.stringify` of the admin SDK class),
 *  or `null`/absent when the integration never ran. */
function lastRunMillis(value: unknown): number | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as {
    seconds?: number;
    _seconds?: number;
    nanoseconds?: number;
    _nanoseconds?: number;
  };
  const seconds = raw.seconds ?? raw._seconds;
  if (seconds === undefined) return null;
  return timestampToMillis({ seconds, nanoseconds: raw.nanoseconds ?? raw._nanoseconds ?? 0 });
}

async function withApi<T>(fn: (apiBase: string, idToken: string) => Promise<T>): Promise<T> {
  const api = getUsersApi();
  const idToken = await api.getAuthToken();
  return fn(api.apiBase, idToken);
}

/** Structural equality for config values — always strings, numbers, string
 *  arrays, or nested maps (never functions/dates), so a `JSON.stringify`
 *  comparison is sufficient without pulling in a deep-equal dependency. Used
 *  by the post-connect re-sync effect below (M1) to tell "the server changed
 *  this field and the owner hasn't touched it since" apart from "the owner is
 *  mid-edit right now". */
function configValueEq(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Il colore della pastiglia di stato.
 *
 * «Connessa» e «non funziona» non possono essere entrambe verdi: il colore è la
 * prima cosa che si legge, e se non distingue costringe a leggere la parola per
 * sapere se c'è un problema.
 */
export function statusTone(stato: string): "success" | "danger" | "warning" | "neutral" {
  if (stato === "connected") return "success";
  if (stato === "error") return "danger";
  if (stato === "needs_attention") return "warning";
  return "neutral";
}

/**
 * L'esito dell'ultima sincronizzazione, detto in italiano.
 *
 * Il motore scrive `ok`, `partial`, `error`: parole per una macchina, che
 * finivano nella scheda così com'erano. «partial» in particolare non si traduce
 * da solo nella testa di chi legge — vuol dire che una parte è entrata e una no,
 * ed è l'unico dei tre che richiede di fare qualcosa.
 */
export function outcomeLabel(stato: string | null, t: Translate): string {
  if (stato === null || stato === "") return "";
  // Gli stati che il motore può produrre hanno una chiave; qualunque altra cosa
  // (per esempio i `invalid_config: …`) si mostra com'è, perché contiene già la
  // spiegazione e inventarci una traduzione la nasconderebbe.
  const noti = ["ok", "partial", "error"];
  return noti.includes(stato) ? t(`settings.integrations.outcome.${stato}`) : stato;
}

/**
 * What the last sync actually brought in, per record kind.
 *
 * Worth a component of its own: a run can report `ok` while importing ZERO
 * absences — which is precisely the failure that went unnoticed for weeks, because
 * the card said "connesso · ok" and nothing contradicted it. The counts are what
 * make it obvious, and the skipped ones are called out because a skip means the
 * record found nobody to attach to.
 *
 * What the owner can DO about a skip depends on the connector, so the advice
 * has to follow `hasMappingTable` rather than assume: the file connector
 * renders an employee-mapping table right below this message, while the API
 * connector owns its `employeeMap` as machine state and renders none. Telling
 * an API-connector owner to "complete the mapping table below" pointed at a
 * section that ends two fields later (Black Fin, 2026-07-21).
 */
function LastRunSummary(props: {
  lastRun: IntegrationSummaryDTO["lastRun"];
  t: Translate;
  hasMappingTable: boolean;
}) {
  const { lastRun, t, hasMappingTable } = props;
  const byKind = lastRun?.byKind;
  if (!byKind || Object.keys(byKind).length === 0) return null;

  const KIND_LABEL: Record<string, string> = {
    reservation: t("settings.integrations.kind.reservation"),
    person: t("settings.integrations.kind.person"),
    employment: t("settings.integrations.kind.employment"),
    balance: t("settings.integrations.kind.balance"),
    leave: t("settings.integrations.kind.leave"),
  };

  const parts = Object.entries(byKind).map(([kind, c]) => ({
    kind,
    label: KIND_LABEL[kind] ?? kind,
    applied: c.created + c.updated,
    skipped: c.skipped,
  }));
  const totalSkipped = parts.reduce((a, p) => a + p.skipped, 0);
  const unmatched = lastRun?.unmatched ?? [];

  return (
    <div className="text-caption text-fg-muted space-y-0.5">
      <p>
        {parts.map((p, i) => (
          <span key={p.kind}>
            {i > 0 ? " · " : ""}
            <span className={p.applied === 0 ? "text-warning" : ""}>
              {p.applied} {p.label}
            </span>
          </span>
        ))}
      </p>
      {totalSkipped > 0 && (
        <p className="text-warning">
          {t(
            hasMappingTable
              ? "settings.integrations.lastRun.skipped"
              : "settings.integrations.lastRun.skippedNoMapping",
            { count: totalSkipped },
          )}
          {/* WHICH employees, not just how many: a count says something is
              missing without saying who, which the owner cannot act on.
              These identifiers can be personal names (DiC derives them from
              the name when its export has no numeric id) — acceptable here
              because this panel is owner-gated and an owner already sees the
              staff roster; see the `unmatched` doc comment before reusing. */}
          {unmatched.length > 0 && (
            <span className="block">
              {t("settings.integrations.lastRun.unmatchedIds", { ids: unmatched.join(", ") })}
            </span>
          )}
        </p>
      )}
    </div>
  );
}

/**
 * Like `FormField`, but for a config field whose control is NOT one single
 * labelable element — a group of independent checkboxes (`multiselect`), or a
 * bare warning paragraph (the `kind: "hotel"` / zero-hotels case). `FormField`
 * clones its generated `id` into the single child it's given and points a
 * `<label htmlFor>` at it; for a `<div>` of checkboxes or a `<p>` that clone
 * lands on a non-labelable element, so the association silently does nothing
 * for screen readers (invisible in tests, since each `Checkbox` still carries
 * its OWN `<label>`). `fieldset`/`legend` is the native, correct construct for
 * "a label over several inputs" — no ui-kit primitive wraps it (checked:
 * `packages/ui-kit/src/index.ts` has no `Fieldset` export), so this
 * replicates `FormField`'s visual rhythm directly with Tailwind, same as
 * `EmployeeMapEditor` above does for its own custom layout.
 */
function FieldGroup(props: {
  readonly label: string;
  readonly required?: boolean;
  readonly error?: string;
  readonly children: ReactNode;
}) {
  const { label, required, error, children } = props;
  return (
    <fieldset className="m-0 space-y-1 border-0 p-0">
      <legend className="text-eyebrow block px-0">
        {label}
        {required && <span className="text-danger ml-0.5">*</span>}
      </legend>
      {children}
      {error && <p className="text-caption text-danger">{error}</p>}
    </fieldset>
  );
}

/**
 * Renders one connector-declared config field. The connector says WHAT to
 * render (`configFields`, from its descriptor); this only knows HOW. Adding a
 * connector therefore needs no change here — which is the point: the id-based
 * branch this replaces (`integration.id === "teamsystem"`) meant
 * `teamsystem-api` rendered no config at all, and connect sent `{}`.
 *
 * `kind: "hotel"` with an empty `hotels` list renders a warning instead of an
 * empty, submittable `<select>` — an empty string would 400 server-side
 * (`defaultHotelId` is `z.string().min(1)` for the api connector) and, worse,
 * a blank value is exactly how the original zero-import bug worked.
 */
function ConfigFieldInput(props: {
  readonly field: ConfigFieldDTO;
  readonly value: unknown;
  readonly hotels: ReadonlyArray<{ id: string; name: string }>;
  readonly error: string | undefined;
  readonly onChange: (value: unknown) => void;
  readonly t: Translate;
}) {
  const { field, value, hotels, error, onChange, t } = props;
  const label = t(`settings.integrations.config.${field.name}`);
  const hasError = error !== undefined;
  const formFieldProps = {
    label,
    ...(field.required ? { required: true } : {}),
    ...(error !== undefined ? { error } : {}),
  };

  switch (field.kind) {
    case "hotel": {
      if (hotels.length === 0) {
        return (
          <FieldGroup {...formFieldProps}>
            <p className="text-caption text-warning">
              {t("settings.integrations.config.noHotels")}
            </p>
          </FieldGroup>
        );
      }
      return (
        <FormField {...formFieldProps}>
          <Select
            size="sm"
            error={hasError}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">{t("settings.integrations.config.selectHotel")}</option>
            {hotels.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </Select>
        </FormField>
      );
    }
    case "multiselect": {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <FieldGroup {...formFieldProps}>
          <div className="flex flex-wrap gap-3">
            {(field.options ?? []).map((opt) => (
              <Checkbox
                key={opt}
                label={t(`settings.integrations.category.${opt}`)}
                checked={selected.includes(opt)}
                onChange={(e) =>
                  onChange(
                    e.target.checked ? [...selected, opt] : selected.filter((s) => s !== opt),
                  )
                }
              />
            ))}
          </div>
        </FieldGroup>
      );
    }
    case "number":
      return (
        <FormField {...formFieldProps}>
          <Input
            type="number"
            size="sm"
            error={hasError}
            {...(field.min !== undefined ? { min: field.min } : {})}
            {...(field.max !== undefined ? { max: field.max } : {})}
            value={typeof value === "number" ? String(value) : ""}
            onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
          />
        </FormField>
      );
    default:
      return (
        <FormField {...formFieldProps}>
          <Input
            size="sm"
            error={hasError}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
          />
        </FormField>
      );
  }
}

/**
 * One integration's card: status + last-sync outcome, a schema-driven
 * credential form when disconnected, and Testa / Sincronizza ora /
 * Disconnetti + cadence once connected.
 */
function IntegrationRow(props: {
  readonly integration: IntegrationSummaryDTO;
  readonly t: Translate;
  readonly onChanged: () => void;
}) {
  const { integration, t, onChanged } = props;
  const { reachable: hotels } = useActiveHotel();
  const hotelOfSource = (() => {
    const h = (integration.config as { hotelId?: unknown } | undefined)?.hotelId;
    if (typeof h !== "string" || h === "") return null;
    return hotels.find((x) => x.id === h)?.name ?? h;
  })();
  const fields = integration.credentialFields;
  // Configurata, non "l'ultima volta è andata bene".
  //
  // Era `status === "connected"`, e quindi una sincronizzazione fallita faceva
  // ricomparire il modulo di collegamento con clientId e clientSecret vuoti: la
  // scheda chiedeva all'albergatore di reinserire le credenziali — che erano
  // giuste — per un guasto che stava nella configurazione. Solo una fonte
  // davvero scollegata chiede di collegarsi.
  const connected = integration.status !== "disconnected";
  // The connector declares its own config fields (`configFields`, Task 3) —
  // rendering follows that list, not a hand-maintained per-id branch. Only
  // `teamsystem` (file) currently declares `employeeMap`; `teamsystem-api`
  // does not (its `employeeMap` is connector-owned state, never rendered).
  const employeeMapField = integration.configFields.find((f) => f.kind === "employeeMap");
  // Only a REQUIRED hotel field blocks connect/save when the tenant has no
  // hotels — the file connector's `defaultHotelId` is legitimately optional
  // (the owner can hand-fill `employeeMap` instead), so it must stay usable
  // even with zero hotels. A field of kind "hotel" still individually shows
  // the "no hotels" message instead of an empty, submittable select either way
  // (see `ConfigFieldInput`).
  const requiresHotel = integration.configFields.some((f) => f.kind === "hotel" && f.required);
  const blocked = requiresHotel && hotels.length === 0;

  const [credentials, setCredentials] = useState<Record<string, string>>({});
  // Pre-filled from the stored `config` (GET /api/integrations) so a reload
  // shows the owner's existing values rather than a blank form.
  const [configValues, setConfigValues] = useState<Record<string, unknown>>(() => ({
    ...(integration.config ?? {}),
  }));
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Pre-filled from the stored `config.employeeMap` (GET /api/integrations)
  // so a reload shows the owner's existing mappings rather than a blank
  // editor. Only read on mount — the id-keyed row in IntegrationsSettings
  // keeps this component instance across refreshes, same as `cadence` below.
  const [employeeMapRows, setEmployeeMapRows] = useState<ReadonlyArray<EmployeeMapRow>>(() =>
    employeeMapRowsFromConfig(integration.config),
  );
  const [cadence, setCadence] = useState<Cadence>(integration.cadence ?? "daily");
  const [busy, setBusy] = useState(false);
  // The last `integration.config` known to match the SERVER — as opposed to
  // `configValues`, the owner's live edit buffer. Lets the effect below tell
  // "the server changed this field and the owner hasn't touched it since"
  // (safe to adopt) apart from "the owner is mid-edit right now" (must not
  // clobber).
  const syncedConfigRef = useRef<Record<string, unknown>>(integration.config ?? {});
  // `configValues`'s `useState` initializer above runs ONCE — this row
  // instance survives `onChanged()` refreshes (see the `employeeMapRows`
  // comment above) — so without this effect the form keeps showing
  // connect-time values forever, even once the server has applied its own
  // defaults (e.g. `hoursPerDay: 8`, all three DiC categories). A
  // just-connected form that still reads "blank" looks unconfigured and
  // invites exactly the click-to-fix that produces `categories: []` (see
  // C1) — so on every `integration.config` change, adopt a field's new
  // server value ONLY when the owner hasn't edited it since the last sync;
  // anything actively mid-edit is left alone.
  useEffect(() => {
    const next = integration.config ?? {};
    // Snapshot BEFORE the mutation below, and close over this snapshot (not
    // the ref) inside the updater: `setConfigValues`'s functional updater
    // isn't guaranteed to run synchronously, so reading `syncedConfigRef.current`
    // from inside it would race against the `syncedConfigRef.current = next`
    // assignment a few lines down — by the time the updater actually ran,
    // "old" and "new" would already be the same object, and nothing would look
    // unedited (the C1-adjacent bug this exact effect exists to avoid: a
    // merge that silently keeps everything blank).
    const prevSynced = syncedConfigRef.current;
    if (configValueEq(next, prevSynced)) return;
    setConfigValues((prev) => {
      const merged: Record<string, unknown> = { ...prev };
      for (const key of Object.keys(next)) {
        if (configValueEq(prev[key], prevSynced[key])) {
          merged[key] = next[key];
        }
      }
      return merged;
    });
    syncedConfigRef.current = next;
  }, [integration.config]);
  const [status, setStatus] = useState<SaveStatus>("idle");
  const [testResult, setTestResult] = useState<TestIntegrationResult | null>(null);
  const [syncResult, setSyncResult] = useState<SyncIntegrationResult | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  /** Client-side required-check mirrors the server's schema. It is not the
   *  gate — `POST /connect` / `PUT /config` re-validate and 400 — it just
   *  avoids a round trip whose only outcome would be an opaque
   *  `invalid_config`, and (for `defaultHotelId`) avoids silently saving the
   *  exact blank value that caused the original zero-import bug. */
  function validate(): boolean {
    const errs: Record<string, string> = {};
    for (const f of integration.configFields) {
      if (f.kind === "employeeMap" || !f.required) continue;
      const v = configValues[f.name];
      if (v === undefined || v === "" || (Array.isArray(v) && v.length === 0)) {
        errs[f.name] = t("settings.integrations.config.required");
      }
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function collectedConfig(): Record<string, unknown> {
    return {
      ...configValues,
      ...(employeeMapField ? { employeeMap: rowsToEmployeeMap(employeeMapRows) } : {}),
    };
  }

  function renderConfigFields(): ReactNode {
    if (integration.configFields.length === 0) return null;
    return (
      <>
        {integration.configFields
          .filter((f) => f.kind !== "employeeMap")
          .map((f) => (
            <ConfigFieldInput
              key={f.name}
              field={f}
              value={configValues[f.name]}
              hotels={hotels}
              error={fieldErrors[f.name]}
              onChange={(v) => setConfigValues((prev) => ({ ...prev, [f.name]: v }))}
              t={t}
            />
          ))}
        {employeeMapField && (
          <EmployeeMapEditor rows={employeeMapRows} onChange={setEmployeeMapRows} t={t} />
        )}
      </>
    );
  }

  async function handleConnect(): Promise<void> {
    if (!validate()) return;
    setBusy(true);
    setStatus("saving");
    try {
      await withApi((apiBase, idToken) =>
        connectIntegration(
          apiBase,
          integration.id,
          { credentials, config: collectedConfig() },
          idToken,
        ),
      );
      setCredentials({});
      setStatus("saved");
      onChanged();
      window.setTimeout(() => setStatus("idle"), 1500);
    } catch {
      setStatus("error");
    } finally {
      setBusy(false);
    }
  }

  /** Editable after connect too — not just at connect time. This is how an
   *  owner fixes the field a `needs_attention` alert told them to fix, without
   *  disconnecting and reconnecting (which would also mean re-entering
   *  credentials). Sent WITHOUT `cadence` (now optional server-side, Task 3):
   *  this save is about `config` only, and must not clobber a cadence set
   *  independently via the dropdown below. */
  async function handleSaveConfig(): Promise<void> {
    if (!validate()) return;
    setBusy(true);
    setStatus("saving");
    try {
      await withApi((apiBase, idToken) =>
        updateIntegrationConfig(apiBase, integration.id, { config: collectedConfig() }, idToken),
      );
      setStatus("saved");
      onChanged();
      window.setTimeout(() => setStatus("idle"), 1500);
    } catch {
      setStatus("error");
    } finally {
      setBusy(false);
    }
  }

  async function handleTest(): Promise<void> {
    setBusy(true);
    setTestResult(null);
    try {
      const result = await withApi((apiBase, idToken) =>
        testIntegration(apiBase, integration.id, idToken),
      );
      setTestResult(result);
    } catch {
      setTestResult({ ok: false });
    } finally {
      setBusy(false);
    }
  }

  async function handleCadenceChange(next: Cadence): Promise<void> {
    setCadence(next);
    setBusy(true);
    setStatus("saving");
    try {
      // Preserve the current stored config verbatim. The server writes
      // `{ cadence, config }` with Firestore `.set(..., { merge: true })`,
      // which DEEP-merges nested maps — so sending `config: {}` here would
      // NOT wipe the previously saved `employeeMap` (merge is a no-op on
      // fields you omit). Re-sending the full config is belt-and-suspenders
      // rather than a data-loss guard: it keeps this editor a faithful
      // round-trip of the existing row (what you see is what gets saved)
      // instead of relying on callers to know which fields merge is safe to
      // omit.
      await withApi((apiBase, idToken) =>
        updateIntegrationConfig(
          apiBase,
          integration.id,
          { cadence: next, config: integration.config ?? {} },
          idToken,
        ),
      );
      setStatus("saved");
      window.setTimeout(() => setStatus("idle"), 1500);
    } catch {
      setStatus("error");
    } finally {
      setBusy(false);
    }
  }

  async function handleSync(): Promise<void> {
    setBusy(true);
    setSyncResult(null);
    try {
      const result = await withApi((apiBase, idToken) =>
        syncIntegration(apiBase, integration.id, idToken),
      );
      setSyncResult(result);
      onChanged();
    } catch {
      setSyncResult({ status: "error", runId: null });
    } finally {
      setBusy(false);
    }
  }

  async function handleDisconnect(): Promise<void> {
    setBusy(true);
    try {
      await withApi((apiBase, idToken) => disconnectIntegration(apiBase, integration.id, idToken));
      onChanged();
    } catch {
      setStatus("error");
    } finally {
      setBusy(false);
    }
  }

  const lastRunMs = lastRunMillis(integration.lastRunAt);

  return (
    <Card padding="sm" className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-fg-default">{integration.displayName}</span>
        {/* Quale struttura, quando il connettore ne serve più d'una.
            Il `displayName` viene dal descrittore, che è per CONNETTORE: con due
            fonti prenotazioni le schede risultavano intitolate identiche —
            «TeamSystem — Prenotazioni» due volte — e si distinguevano solo dal
            numero di prenotazioni. Con tre strutture non si distinguono affatto.
            Si vede solo avendone davvero due: fino a ieri Prospero ne aveva una. */}
        {hotelOfSource !== null && (
          <span className="text-body-sm text-fg-muted" data-testid={`struttura-${integration.id}`}>
            {hotelOfSource}
          </span>
        )}
        <Badge tone={statusTone(integration.status)} variant="soft" size="xs">
          {t(`settings.integrations.status.${integration.status}`)}
        </Badge>
      </div>
      <p className="text-caption text-fg-muted">
        {lastRunMs !== null
          ? `${t("settings.integrations.lastSync")} ${relativeTime(lastRunMs, t)} · ${outcomeLabel(
              integration.lastStatus,
              t,
            )}`
          : t("settings.integrations.neverSynced")}
      </p>

      {/* Il motivo, quando c'è qualcosa da dire. Prima la scheda diceva
          «error» — in inglese, e senza aggiungere niente a ciò che il colore
          già mostrava: chi non scrive codice non aveva modo di sapere se
          toccava a lui fare qualcosa o aspettare. */}
      {integration.lastError !== null && integration.lastError !== "" && (
        <p
          className="rounded-md border border-danger/40 bg-danger-soft p-2 text-caption text-fg-default"
          data-testid={`integration-error-${integration.id}`}
        >
          <span className="font-medium">{t("settings.integrations.errorReason")}</span>{" "}
          {integration.lastError}
        </p>
      )}

      <LastRunSummary
        lastRun={integration.lastRun ?? null}
        t={t}
        hasMappingTable={employeeMapField !== undefined}
      />

      {!connected ? (
        <div className="space-y-3">
          <div className="space-y-2">
            {fields.map((field) => (
              <FormField key={field} label={field}>
                <Input
                  type={SENSITIVE_FIELD_RE.test(field) ? "password" : "text"}
                  autoComplete="off"
                  value={credentials[field] ?? ""}
                  onChange={(e) => setCredentials((prev) => ({ ...prev, [field]: e.target.value }))}
                />
              </FormField>
            ))}
          </div>
          <div className="space-y-2">{renderConfigFields()}</div>
          <Button
            size="sm"
            variant="primary"
            onClick={() => void handleConnect()}
            disabled={busy || blocked}
          >
            {t("settings.integrations.action.connect")}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <FormField label={t("settings.integrations.cadence")}>
              <Select
                size="sm"
                value={cadence}
                disabled={busy}
                onChange={(e) => void handleCadenceChange(e.target.value as Cadence)}
              >
                <option value="hourly">{t("settings.integrations.cadence.hourly")}</option>
                <option value="operativa">{t("settings.integrations.cadence.operativa")}</option>
                <option value="mattina_e_sera">
                  {t("settings.integrations.cadence.mattina_e_sera")}
                </option>
                <option value="daily">{t("settings.integrations.cadence.daily")}</option>
              </Select>
            </FormField>
            <Button size="sm" variant="secondary" onClick={() => void handleTest()} disabled={busy}>
              {t("settings.integrations.action.test")}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => void handleSync()} disabled={busy}>
              {t("settings.integrations.action.sync")}
            </Button>
            {integration.ingestion === "file" && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setUploadOpen(true)}
                disabled={busy}
              >
                {t("settings.integrations.action.uploadExport")}
              </Button>
            )}
            <Button
              size="sm"
              variant="danger"
              onClick={() => void handleDisconnect()}
              disabled={busy}
            >
              {t("settings.integrations.action.disconnect")}
            </Button>
          </div>
          {integration.configFields.length > 0 && (
            <div className="space-y-2">
              {renderConfigFields()}
              <Button
                size="sm"
                variant="secondary"
                onClick={() => void handleSaveConfig()}
                disabled={busy || blocked}
              >
                {t("settings.integrations.config.save")}
              </Button>
            </div>
          )}
        </div>
      )}

      {uploadOpen && (
        <UploadExportModal
          integrationId={integration.id}
          t={t}
          onClose={() => setUploadOpen(false)}
          onUploaded={() => {
            setUploadOpen(false);
            onChanged();
          }}
        />
      )}

      {testResult && (
        <p className={`text-caption ${testResult.ok ? "text-success" : "text-danger"}`}>
          {testResult.ok
            ? t("settings.integrations.test.ok")
            : `${t("settings.integrations.test.failed")}${
                testResult.message ? ` — ${testResult.message}` : ""
              }`}
        </p>
      )}
      {syncResult && (
        <p className="text-caption text-fg-muted">
          {t("settings.integrations.sync.result", { status: syncResult.status })}
        </p>
      )}
      {status === "error" && <p className="text-caption text-danger">{t("settings.error")}</p>}
    </Card>
  );
}

/**
 * "Integrazioni" — owner-only admin panel listing the tenant's enabled
 * integrations (Task 12's commercial catalogue, `settings/general.
 * enabledIntegrations`) joined with per-integration status. Mounted inside
 * `TenantSettings` (owner tab) next to `NotificationRulesSettings`, same
 * self-gating pattern (`core.rbac.can("manage","hotel")`) — the server
 * (`integrationsApi`, Task 7) independently re-verifies `tenant_owner` on
 * every call, this is defense-in-depth, not the real gate.
 *
 * Credentials are write-only from the client's perspective: `GET
 * /api/integrations` never returns them, and this component never logs the
 * `credentials` object it sends on connect.
 */
export function IntegrationsSettings() {
  const core = useCore();
  const t = core.i18n.t;
  const isOwner = core.rbac.can("manage", "hotel");

  const [integrations, setIntegrations] = useState<ReadonlyArray<IntegrationSummaryDTO> | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(false);

  async function refresh(): Promise<void> {
    try {
      const { integrations: list } = await withApi((apiBase, idToken) =>
        listIntegrations(apiBase, idToken),
      );
      setIntegrations(list);
      setListError(false);
    } catch {
      setListError(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isOwner) {
      setLoading(false);
      return;
    }
    void refresh();
    // Fetch once on mount; `refresh` is re-created per render but the fetch
    // itself is idempotent and re-triggered explicitly by row actions.
  }, [isOwner]);

  if (!isOwner) return null;

  return (
    <Card>
      <SectionHeading
        level={3}
        title={t("settings.integrations.title")}
        description={t("settings.integrations.description")}
      />
      {loading ? (
        <SkeletonFormSection fields={2} />
      ) : listError ? (
        <p className="text-sm text-danger">{t("settings.error")}</p>
      ) : integrations && integrations.length === 0 ? (
        <EmptyState title={t("settings.integrations.empty")} size="compact" />
      ) : (
        <div className="space-y-4">
          {(integrations ?? []).map((integration) => (
            <IntegrationRow
              key={integration.id}
              integration={integration}
              t={t}
              onChanged={() => void refresh()}
            />
          ))}
        </div>
      )}
    </Card>
  );
}
